import { createFileRoute } from '@tanstack/react-router'
import { Trans } from '@lingui/react/macro'
import { ButtonFilled } from '@vgw/multibrand-fe-button'
import { Link } from '@vgw/multibrand-fe-link'
import { Checkbox } from '@vgw/multibrand-fe-checkbox'
import { getBrandConfig } from '@/config/config'
import { AccountVerificationLayout } from '@/routes/_app/account-verification/-layout'
import type { FieldValues, FieldPath } from 'react-hook-form'
import { Controller, useForm, Control } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import * as FormSection from '@vgw/multibrand-fe-form-section'
import { ReactNode } from 'react'
import { zodValidator } from '@tanstack/zod-adapter'

export const Route = createFileRoute('/_app/account-verification_/tnc')({
  component: RouteComponent,
  validateSearch: zodValidator(
    z.object({
      url: z.string(),
    }),
  ),
})

const TncSchema = z.object({
  item1: z.boolean().refine((val) => val),
  item2: z.boolean().refine((val) => val),
  item3: z.boolean().refine((val) => val),
})

interface TncFieldValues extends z.infer<typeof TncSchema> {}

const CheckboxItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  children,
}: {
  children: ReactNode
  name: TName
  control: Control<TFieldValues>
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { value, onChange, onBlur, name },
        fieldState: { invalid },
      }) => (
        <FormSection.Root>
          <Checkbox
            name={name}
            checked={value}
            onCheckedChange={onChange}
            onBlur={onBlur}
            hasError={invalid}
          >
            <div className="text-sm leading-5 font-normal">{children}</div>
          </Checkbox>
        </FormSection.Root>
      )}
    />
  )
}

function RouteComponent() {
  const { control, handleSubmit, formState } = useForm<TncFieldValues>({
    resolver: zodResolver(TncSchema),
  })
  const { url } = Route.useSearch()
  const navigate = Route.useNavigate()

  const { pamLegalDocumentsUrl, name: brandName } = getBrandConfig()

  return (
    <AccountVerificationLayout>
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(() => {
          navigate({ to: '../flow', search: { url } })
        })}
      >
        <div className="flex flex-col gap-2">
          <CheckboxItem name="item1" control={control}>
            <Trans>
              I confirm the following:
              <ul className="ml-6 list-disc">
                <li>I am at least 21 years old;</li>
                <li>
                  I do not have any other {brandName} accounts and understand{' '}
                  {brandName} has the right to cancel all duplicate accounts and
                  withhold winnings made using such accounts; and
                </li>
                <li>
                  I agree to the{' '}
                  <Link
                    weight="regular"
                    size="sm"
                    href={`${pamLegalDocumentsUrl}/terms-and-conditions`}
                    target="_blank"
                    rel="noopener"
                  >
                    Terms & Conditions
                  </Link>{' '}
                  and{' '}
                  <Link
                    weight="regular"
                    size="sm"
                    href={`${pamLegalDocumentsUrl}/sweeps-rules`}
                    target="_blank"
                    rel="noopener"
                  >
                    Sweeps Rules.
                  </Link>
                </li>
              </ul>
            </Trans>
          </CheckboxItem>

          <CheckboxItem name="item2" control={control}>
            <Trans>
              I understand that:
              <ul className="ml-6 list-disc">
                <li>
                  If I live in an <strong>Excluded Territory</strong> (as
                  defined in the Terms & Conditions), I am not eligible to
                  participate in Standard or Promotional Play; and
                </li>
                <li>
                  If I am not an <strong>Eligible Participant</strong> (as
                  defined in the Sweeps Rules) I am not eligible to participate
                  in Promotional Play.
                </li>
              </ul>
            </Trans>
          </CheckboxItem>

          <CheckboxItem name="item3" control={control}>
            <Trans>
              I confirm that all information I provide to {brandName} is true
              and correct and agree to provide any additional verification
              information requested by {brandName}.
            </Trans>
          </CheckboxItem>

          {!formState.isValid && formState.isSubmitted ? (
            <p className="text-sm leading-5 font-normal text-form-error-content">
              <Trans>
                You must read and accept before verifying your account
              </Trans>
            </p>
          ) : null}
        </div>
        <ButtonFilled className="mx-auto w-full lg:max-w-md" type="submit">
          <Trans>Continue</Trans>
        </ButtonFilled>
      </form>
    </AccountVerificationLayout>
  )
}
